import { Fragment, Key } from 'react';
import BrandGroup from '@apps/components/BrandGroup';
import FuelPriceBox from '@apps/components/FuelPriceBox';
import ImageContent from '@apps/components/ImageContent';
import RunningNumbersBox from '@apps/components/RunningNumbersBox';
import ShowMoreButton from '@apps/components/ShowMoreButton';
import { BASE_URL } from '@apps/config';
import { BrandItemType } from '@apps/config/brand.config';
import AdsPopup from '@apps/containers/AdsPopup';
import { IBrand, IPost } from '@apps/types';
import { dateConversions } from '@apps/utils/date-conversion';
import { getDateSlug } from '@apps/utils/get-date-slug';
import { localeConversions } from '@apps/utils/locale-conversion';
import placeholderImage from '@stack/assets/images/empty_placeholder.jpg';
import { TitleDivider } from '@stack/ui/components';
import { toLower } from 'lodash';
import { useAmp } from 'next/amp';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { NewsDetailSection } from '../../containers/NewsDetailSection/index';
import {
  ContentWrapper,
  InnerContentWrapper,
} from '../../containers/NewsListing/styles';
import { IPostWithoutTranslation } from '../../types/index';

import {
  Error404Button,
  Error404Caption,
  Error404CaptionWrapper,
  Error404Container,
  Error404Title,
  LeftSectionWrapper,
  NewsDetailContainer,
  NewsDetailListingWrapper,
  NewsDetailSectionWrapper,
  RightSectionWrapper,
} from './styles';
const AdsContainer = dynamic<any>(
  () => import('@apps/containers/AdsContainer'),
  {
    ssr: true,
  }
);

export type NewsDetailScreenProps = {
  relatedNews: any;
  data: IPost;
  listing: IPostWithoutTranslation[];
  brands: IBrand[];
  fuels: any;
  jpjNumber?: any;
  webView?: boolean;
  ads?: any;
};
/**
 * ===========================
 * MAIN
 * ===========================
 */
export const NewsDetailScreen: React.FC<NewsDetailScreenProps> = (props) => {
  const { data, listing, brands, fuels, jpjNumber, webView, relatedNews } =
    props;

  // ======================= HOOKS
  const router = useRouter();

  const { lang, t } = useTranslation('common');

  // ======================= STATE

  // ======================= API

  // ======================= EVENTS

  // ======================= VARIABLES
  const availableLang = data?.post_available_language as string;
  const newDays = dateConversions(data?.post_created, true) as string;
  const newLang = localeConversions(lang);
  const hasData = availableLang?.includes(newLang);
  const locale = hasData ? newLang : 'en';
  const isAmp = useAmp();

  const shareUrl = (post: IPost) => {
    const dateSlug = getDateSlug(post?.post_created?.split(' ')[0]);

    if (router?.locale && router.locale !== 'en') {
      return `${BASE_URL}/${router?.locale}/${dateSlug}/${post?.post_slug}`;
    }

    return `${BASE_URL}/${dateSlug}/${post?.post_slug}`;
  };

  const getPostHref = (post: any) => {
    if (!post?.post_title) return;
    const dateSlug = getDateSlug(post.post_created);
    return webView
      ? `/webview/${dateSlug}/${toLower(post?.post_slug)}`
      : `/${dateSlug}/${toLower(post?.post_slug)}`;
  };
  // ======================= RENDER FUNCTIONS
  // ======================= VIEWS
  return (
    <Fragment>
      <AdsPopup webView={props?.webView} localPlacement={'news_home_popup'} />
      <NewsDetailContainer>
        <NewsDetailSectionWrapper>
          <LeftSectionWrapper $fullWidth={webView}>
            {hasData ? (
              <>
                <NewsDetailSection
                  title={data?.post_title[locale]}
                  category={data?.category_name[locale]}
                  authorName={data?.post_author[locale]}
                  authorUsername={data?.post_author_username[locale]}
                  authorAvatar={data?.post_author_profile_image[locale]}
                  authorDescription={data?.post_authors_description[locale]}
                  createdDate={newDays}
                  coverImage={data?.post_image_full[locale]}
                  content={data?.post_description[locale]}
                  tags={
                    data?.post_tag?.map((tag) => {
                      return {
                        label: tag.post_tag_name['en'], // Hard coded en tag as backend only support en
                        slug: tag.post_tag_slug,
                      };
                    }) ?? []
                  }
                  shareUrl={shareUrl(data)}
                  nextNews={{
                    title: data?.next?.post_title?.[locale] ?? '',
                    slug: data?.next?.post_slug,
                    avatar: data?.next?.authors_image?.[locale] ?? '',
                    id: data?.next?.post_id,
                    createdAt: data?.next?.post_created,
                  }}
                  prevNews={{
                    title: data?.previous?.post_title?.[locale] ?? '',
                    slug: data?.previous?.post_slug,
                    avatar: data?.previous?.authors_image?.[locale] ?? '',
                    id: data?.previous?.post_id,
                    createdAt: data?.previous?.post_created,
                  }}
                  galleryImages={data?.post_images?.all ?? []}
                  webView={webView}
                />
              </>
            ) : (
              <>
                <Error404Container
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <div
                    style={{
                      top: '20%',
                      width: '100%',
                      position: 'absolute',
                    }}
                  >
                    <Error404Title>404</Error404Title>
                    <Error404CaptionWrapper>
                      <Error404Caption>{t('404caption')}</Error404Caption>
                    </Error404CaptionWrapper>
                    {!webView && (
                      <Link href={'/'}>
                        <Error404Button>{t('backHome')}</Error404Button>
                      </Link>
                    )}
                  </div>
                </Error404Container>
              </>
            )}
          </LeftSectionWrapper>
          {!webView && (
            <RightSectionWrapper>
              {/* Ads Section */}
              {!webView && (
                <div
                  className="adsbygoogle"
                  style={
                    isAmp
                      ? {
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#F7F7F7',
                          width: '100%',
                          minHeight: '270px',
                        }
                      : {
                          backgroundColor: '#F7F7F7',
                        }
                  }
                >
                  {/* <AdSense
                  adSlot={'5942712071'}
                  style={{
                    display: 'block',
                  }}
                /> */}
                  <AdsContainer
                    adSlot={'5942712071'}
                    style={{
                      display: 'block',
                      width: '100%',
                      minHeight: '270px',
                      height: '100%',
                    }}
                    localPlacement={'sm_details'}
                  />
                </div>
              )}
              {!webView && (
                <BrandGroup
                  brands={brands as unknown as BrandItemType[]}
                  webView={webView}
                />
              )}
              {!webView && (
                <RunningNumbersBox
                  lastUpdatedAt={jpjNumber?.last_updated}
                  runningNumbers={jpjNumber?.data}
                  webView={webView}
                />
              )}
              {!webView && (
                <FuelPriceBox
                  lastUpdatedAt="25-02-2024 (WEDNESDAY)  6:00PM"
                  fuels={fuels}
                />
              )}
            </RightSectionWrapper>
          )}
        </NewsDetailSectionWrapper>
        {/* relatedNews */}
        {relatedNews && relatedNews.length > 0 && (
          <TitleDivider title={t('newsDetail.relatedNews')}>
            <NewsDetailListingWrapper
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 30,
              }}
            >
              <ContentWrapper className="grid grid-5-col">
                {relatedNews?.map((rd: any, index: Key | null | undefined) => {
                  return (
                    <InnerContentWrapper
                      className="list-item custom-list-border"
                      key={index}
                    >
                      <ImageContent
                        title={rd.post_title?.[locale]}
                        titleSlug={rd?.post_slug}
                        description={rd.post_short_description?.[locale]}
                        badgeLabel={rd.category_name?.[locale]}
                        footerText={rd.post_created.split(' ')[0]}
                        imageProps={{
                          src:
                            rd?.post_thumbnail_id?.[locale] ||
                            placeholderImage.src,
                          width: 600,
                          height: 315,
                          alt: rd?.post_title?.[locale],
                        }}
                        imagePosition="top"
                        author={rd?.post_author}
                        authorAvatar={rd?.post_author_profile_image}
                        authorEmail={rd?.post_author_username}
                        simpleMbView={true}
                        simpleMobileView={true}
                        titleClass="custom-text-xl"
                        mobileImagePostion="left"
                        href={getPostHref(rd)}
                        webView={webView}
                      />
                    </InnerContentWrapper>
                  );
                })}
              </ContentWrapper>
            </NewsDetailListingWrapper>
          </TitleDivider>
        )}
        {/* listing */}
        {listing && listing.length > 0 && (
          <TitleDivider title={t('latestNews')}>
            <NewsDetailListingWrapper
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 30,
              }}
            >
              <ContentWrapper className="grid grid-5-col">
                {listing?.map((rd, index) => {
                  return (
                    <InnerContentWrapper
                      className="list-item custom-list-border"
                      key={index}
                    >
                      <ImageContent
                        title={rd.post_title}
                        titleSlug={rd?.post_slug}
                        description={rd.post_short_description}
                        badgeLabel={rd.category_name}
                        footerText={rd.post_created.split(' ')[0]}
                        imageProps={{
                          src: rd?.post_thumbnail_id || placeholderImage.src,
                          width: 600,
                          height: 315,
                          alt: rd?.post_title,
                        }}
                        imagePosition="top"
                        author={rd?.post_author}
                        authorAvatar={rd?.post_author_profile_image}
                        authorEmail={rd?.post_author_username}
                        simpleMbView={true}
                        simpleMobileView={true}
                        titleClass="custom-text-xl"
                        mobileImagePostion="left"
                        href={getPostHref(rd)}
                        webView={webView}
                      />
                    </InnerContentWrapper>
                  );
                })}
              </ContentWrapper>
              {!webView && (
                <ShowMoreButton
                  href={
                    webView
                      ? `/webview/${data?.category_slug}`
                      : `/${data?.category_slug}`
                  }
                />
              )}
              {webView && <br />}
            </NewsDetailListingWrapper>
          </TitleDivider>
        )}
      </NewsDetailContainer>
    </Fragment>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default NewsDetailScreen;
